/**
 * "Review line" in Oasis montaj (MB's R59, decision 539; spec docs/specs/geosoft-qc.md §10): the view script that
 * shows a flagged line's problem, from the aircraft type's script set in the project's `Geosoft\Scripts` folder
 * (decision 504). MB's list (25 Oct 2026):
 *
 * | Issue | QC tests | Cessna 208 script (`QC_Mag_Database_Gradient`) |
 * |---|---|---|
 * | Tail mag | mag noise, daily noise of MagTF1U, sample interval | `First_View_Gradient.gs` |
 * | Wing-tip mags 2 and 3 | daily noise of MagTF2U and MagTF3U | `View_Gradient.gs` |
 * | Height, GPS and DGPS | terrain clearance, flight path, GPS jumps, DGPS, satellites, hard deck, lower working height, speed | `EDIT_QC_HT_Above_DTM_MB.gs` |
 * | Crystals and sample time | Dn, Samp | `VIEW_RAD4ch_DTM_Gradient.gs` |
 * | MagTF1U against VecTF | mag ratio (mag / fluxgate total field) | `VIEW_FLUX_TMI_1_Gradient.gs` |
 * | BaroHPa, Temp, Humid | the analogue statistics (decision 504, to come) | `View_Analogs_Load_View.gs` |
 *
 * The Cessna 210 (`C210_Scripts_V2.10`) and helicopter (`Heli_Scripts_V1.00`) sets have the same five views, numbered
 * 1 to 5; with one magnetometer, the wing-tip issue opens the first view. Teslyn Field lists the folder's scripts
 * (`geosoft_review_scripts`) and this chooses one; the core opens it (`geosoft_review_open`).
 */
import type { QcEvent, QcFamily, QcLineResult, QcVerdict } from './geosoft-qc.js';

export type QcReviewIssue =
  'tail_mag' | 'wingtip_mags' | 'height_gps' | 'radiometrics' | 'mag_vs_vector' | 'analogues';

export const QC_REVIEW_ISSUE_WORDS: Readonly<Record<QcReviewIssue, string>> = {
  tail_mag: 'Tail mag',
  wingtip_mags: 'Wing-tip mags 2 and 3',
  height_gps: 'Height, GPS and DGPS',
  radiometrics: 'Crystals (Dn) and sample time (Samp)',
  mag_vs_vector: 'MagTF1U against VecTF',
  analogues: 'BaroHPa, Temp and Humid',
};

/** The script folder of each aircraft type under `Geosoft\Scripts` (decision 504; matched whatever the case). */
export const QC_SCRIPT_FOLDERS: Readonly<Record<QcFamily, string>> = {
  c208: 'QC_Mag_Database_Gradient',
  c210: 'C210_Scripts_V2.10',
  heli: 'Heli_Scripts_V1.00',
};

/** The views folder of each aircraft type (decision 504: `Gradient_Views`, `C210_Views_V2.10`, `Heli_Views_V1.00`). */
export const QC_VIEW_FOLDERS: Readonly<Record<QcFamily, string>> = {
  c208: 'Gradient_Views',
  c210: 'C210_Views_V2.10',
  heli: 'Heli_Views_V1.00',
};

const HEIGHT_GPS = new Set([
  'terrain_clearance',
  'flight_path',
  'gps_jump_east',
  'gps_jump_north',
  'gps_height_jump',
  'dgps',
  'satellites',
  'hard_deck',
  'lower_working_height',
  'speed',
]);

/** The issue a QC test points at (MB's list); an unknown test opens the first view. */
export function qcReviewIssueOfTest(key: string, label: string): QcReviewIssue {
  const k = key.toLowerCase();
  if (HEIGHT_GPS.has(k)) return 'height_gps';
  if (k === 'crystals' || k === 'spectrometer_timing' || /(^|_)(dn|up|samp)(_|$)/.test(k))
    return 'radiometrics';
  if (k === 'mag_ratio' || /vec/.test(k)) return 'mag_vs_vector';
  if (/baro|temp|humid/.test(k)) return 'analogues';
  if (k.startsWith('daily_noise') || k.startsWith('mag')) {
    if (/MagTF[23]/i.test(label) || /magtf[23]/.test(k)) return 'wingtip_mags';
    return 'tail_mag';
  }
  return 'tail_mag';
}

/** The issues of a flagged line, the failing tests' first, each once; none for a line that passed. */
export function qcReviewIssues(line: QcLineResult): QcReviewIssue[] {
  const out: QcReviewIssue[] = [];
  for (const status of ['fail', 'warn'] as const)
    for (const t of line.tests)
      if (t.status === status) {
        const issue = qcReviewIssueOfTest(t.key, t.label);
        if (!out.includes(issue)) out.push(issue);
      }
  return out;
}

/** Leading numbers of the C208 names (`1A. `, `1B.`, `2.1`, `3.`): MB names the scripts without them. */
const NUMBERED = String.raw`(?:\d+(?:\.\d+)?(?:[A-Z](?=[.\s]))?\.?\s*)?`;
const named = (name: string): RegExp => new RegExp(`^${NUMBERED}${name.replace(/[.]/g, '\\.')}$`, 'i');

/** Each issue's script, in order of preference, per aircraft type. */
const SCRIPTS: Readonly<Record<QcFamily, Readonly<Record<QcReviewIssue, readonly RegExp[]>>>> = {
  c208: {
    tail_mag: [named('First_View_Gradient.gs')],
    wingtip_mags: [named('View_Gradient.gs')],
    // MB's example was QC_HT_Above_DTM_MB.gs; the folder has EDIT_QC_HT_Above_DTM_MB.gs
    height_gps: [named('EDIT_QC_HT_Above_DTM_MB.gs'), named('QC_HT_Above_DTM_MB.gs')],
    radiometrics: [named('VIEW_RAD4ch_DTM_Gradient.gs')],
    mag_vs_vector: [named('VIEW_FLUX_TMI_1_Gradient.gs')],
    analogues: [named('View_Analogs_Load_View.gs')],
  },
  c210: {
    tail_mag: [/^C210_Script_1A_.*First_View.*\.gs$/i],
    wingtip_mags: [/^C210_Script_1A_.*First_View.*\.gs$/i],
    height_gps: [/^C210_Script_2_.*Elevation_QC_MB\.gs$/i, /^C210_Script_2_.*\.gs$/i],
    radiometrics: [/^C210_Script_3_.*8dn\.gs$/i, /^C210_Script_3_.*\.gs$/i],
    mag_vs_vector: [/^C210_Script_4_.*\.gs$/i],
    analogues: [/^C210_Script_5_.*\.gs$/i],
  },
  heli: {
    tail_mag: [/^Heli_Script_1_.*First_View\.gs$/i],
    wingtip_mags: [/^Heli_Script_1_.*First_View\.gs$/i],
    height_gps: [/^Heli_Script_2_.*_MB\.gs$/i, /^Heli_Script_2_.*\.gs$/i],
    radiometrics: [/^Heli_Script_3_.*8dn\.gs$/i, /^Heli_Script_3_.*\.gs$/i],
    mag_vs_vector: [/^Heli_Script_4_.*\.gs$/i],
    analogues: [/^Heli_Script_5_.*\.gs$/i],
  },
};

/** The script file (a name in the family's folder) that shows `issue`, or null when the folder has none. */
export function qcReviewScript(
  family: QcFamily,
  issue: QcReviewIssue,
  names: readonly string[],
): string | null {
  const sorted = [...names].filter((n) => /\.gs$/i.test(n)).sort((a, b) => a.localeCompare(b));
  for (const re of SCRIPTS[family][issue]) {
    const hit = sorted.find((n) => re.test(n));
    if (hit) return hit;
  }
  return null;
}

// ---------------------------------------------------------------------------------- every breach's view (R117)

/** One breach of a line: a failing or check test and its events. */
export interface QcReviewBreach {
  readonly key: string;
  readonly label: string;
  readonly status: Extract<QcVerdict, 'fail' | 'warn'>;
  readonly events: readonly QcEvent[];
}

/** One view "Review line" shows for a line, and the breaches it shows. */
export interface QcReviewViewPlan {
  /** The issues it shows (several when they share the script: the C210's tail and wing-tip mags). */
  readonly issues: readonly QcReviewIssue[];
  /** The script in the aircraft type's folder; null when the folder has none for these issues. */
  readonly script: string | null;
  /** Its breaches, fails first, each once. */
  readonly breaches: readonly QcReviewBreach[];
  /** A flight path breach is among them: the flight path map goes under the plots. */
  readonly flightPath: boolean;
}

/**
 * MB's R117 (27 Oct 2026): "Review line" shows every breach's view, stacked. A line's breaches (its failing tests,
 * then its checks) each have their issue's view script; breaches whose scripts are the same share one view
 * (decision 578: a flight path and a terrain clearance breach are both in the height and GPS view), so a view is
 * made and kept once (keyed by the view, `qcReviewViewId`) and names every breach it shows. A line that passed has
 * none.
 */
export function qcReviewPlan(
  line: QcLineResult,
  family: QcFamily,
  names: readonly string[],
): QcReviewViewPlan[] {
  const out: { issues: QcReviewIssue[]; script: string | null; breaches: QcReviewBreach[] }[] = [];
  for (const status of ['fail', 'warn'] as const)
    for (const t of line.tests) {
      if (t.status !== status) continue;
      const issue = qcReviewIssueOfTest(t.key, t.label);
      const script = qcReviewScript(family, issue, names);
      const at = out.find((v) =>
        script ? v.script === script : v.script === null && v.issues.includes(issue),
      );
      const breach: QcReviewBreach = { key: t.key, label: t.label, status, events: t.events };
      if (at) {
        if (!at.issues.includes(issue)) at.issues.push(issue);
        if (!at.breaches.some((b) => b.key === t.key)) at.breaches.push(breach);
      } else out.push({ issues: [issue], script, breaches: [breach] });
    }
  return out.map((v) => ({ ...v, flightPath: v.breaches.some((b) => b.key === 'flight_path') }));
}

/** The view the core keeps a picture under: the script folder and the script (`QC_Mag_Database_Gradient/2.1…gs`). */
export function qcReviewViewId(folder: string, script: string): string {
  return `${folder}/${script}`;
}

/** The issues' words inside a sentence (channel names keep their case). */
const ISSUE_IN_SENTENCE: Readonly<Record<QcReviewIssue, string>> = {
  tail_mag: 'tail mag',
  wingtip_mags: 'wing-tip mags 2 and 3',
  height_gps: 'height, GPS and DGPS',
  radiometrics: 'crystals (Dn) and sample time (Samp)',
  mag_vs_vector: 'MagTF1U against VecTF',
  analogues: 'BaroHPa, Temp and Humid',
};

/** "Flight path" → "flight path" in a sentence; "DGPS corrections lost" and channel names stay as they are. */
const inSentence = (label: string): string =>
  /^[A-Z][a-z]/.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label;

/** "Flight path and terrain clearance: the height, GPS and DGPS view". */
export function qcReviewViewTitle(v: QcReviewViewPlan): string {
  const names = v.breaches.map((b, i) => (i === 0 ? b.label : inSentence(b.label)));
  const what =
    names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  const view = v.issues.map((i) => ISSUE_IN_SENTENCE[i]).join(' and ');
  return `${what}: the ${view} view`;
}

/**
 * The rows of a picture an event covers (its zFid start and end among the picture's fids), to shade the breach on the
 * plots; null when the event is not on them.
 */
export function qcEventRows(e: QcEvent, fids: readonly number[]): [number, number] | null {
  let first = -1;
  let last = -1;
  for (let i = 0; i < fids.length; i++) {
    const f = fids[i]!;
    if (f >= e.fid_start && f <= e.fid_end) {
      if (first < 0) first = i;
      last = i;
    }
  }
  return first < 0 ? null : [first, last];
}
